import { GENRESPATH, CLIENTPATH } from '../constants/paths';
import { useCollectionCache } from '../services/collectionCache.service';
import { SearchFilter } from '../types/filter';
import { collection, getDocs, getDoc, doc } from 'firebase/firestore';

interface MappingItem {
  naturalTerms: string[];
  filterType: string;
  value: any;
}

// Define proper interfaces to match your application's filter structure
interface FilterValue {
  label: string;
  value: string;
}

export interface SubFilter {
  key: string;
  path: string;
  value: FilterValue;
}

interface DirectMatch {
  filter: SearchFilter;
  confidence: number;
  matchType: 'direct' | 'mapped' | 'context';
}

interface LicenseKeyword {
  type: string;
  value: string;
}

interface LicenseKeywords {
  [key: string]: LicenseKeyword;
}

class FilterMapper {
  private mappings: MappingItem[] = [];
  private isLoaded = false;
  
  async loadMappings(firebase: any): Promise<void> {
    if (this.isLoaded) return;
    try {
      const snapshot = await getDoc(doc(firebase.db, 'chatbotMappings', 'default'));
      if (snapshot.exists()) {
        const data = snapshot.data();
        this.mappings = data?.mappings || [];
        this.isLoaded = true;
      }
    } catch (error) {
      console.error('Error loading mappings:', error);
      throw error;
    }
  }
  
  findMapping(term: string): MappingItem | null {
    const normalizedTerm = term.toLowerCase().trim();
    return this.mappings.find(mapping => 
      mapping.naturalTerms.some(t => 
        t.toLowerCase() === normalizedTerm
      )
    ) || null;
  }

  async findDirectMatches(keywords: string[], firebase: any): Promise<DirectMatch[]> {
    await this.loadMappings(firebase);
    const matches: DirectMatch[] = [];
    const processedKeys = new Set<string>();
    for (const keyword of keywords) {
      // Try exact matches in database
      const directMatches = await this.searchLocalCache(keyword, firebase);
      if (directMatches && directMatches.length > 0) {
        directMatches.forEach(match => {
          matches.push({
            filter: match.filter,
            confidence: match.isExactMatch ? 1.0 : 0.8,
            matchType: 'direct'
          });
        });
        processedKeys.add(keyword.toLowerCase());
      }
      if (!processedKeys.has(keyword.toLowerCase())) {
        const mappedMatch = this.findMapping(keyword);
        if (mappedMatch) {
          const filter = await this.convertMappingToFilter(mappedMatch);
          matches.push({
            filter,
            confidence: 0.8,
            matchType: 'mapped'
          });
          processedKeys.add(keyword.toLowerCase());
        }
      }
    }
    return matches;
  }

  private async searchLocalCache(keyword: string, firebase: any): Promise<Array<{filter: SearchFilter, isExactMatch: boolean}> | null> {
    try {
      // Get the collection cache state
      const collectionCache = useCollectionCache.getState();
      if (!collectionCache.isInitialized) {
        return this.searchDatabase(keyword, firebase);
      }
      const collections = ['genres', 'client', 'studio', 'composers', 'creativedirector', 'producer', 'sessionartists', 'agency', 'keys', 'tags'];
      const allMatches: Array<{filter: SearchFilter, isExactMatch: boolean}> = [];
      for (const collection of collections) {
        const cachedData = collectionCache[collection as keyof typeof collectionCache] as import('../services/collectionCache.service').CollectionData[];
        if (!cachedData) {
          continue;
        }
        // For genres and tags, only allow exact matches
        const isStrictMatch = collection === 'genres' || collection === 'tags';
        const matchedItems = Array.isArray(cachedData)
          ? cachedData.filter((item: any) => {
              // Only match objects with displayName/label/name
              if (typeof item !== 'object' || item === null) return false;
          const label = item.displayName || item.label || item.name || '';
          const normalizedLabel = label.toLowerCase();
          const normalizedKeyword = keyword.toLowerCase();
          if (normalizedLabel === normalizedKeyword) {
            return true;
          }
          if (!isStrictMatch) {
            const wordBoundaryRegex = new RegExp(`\\b${normalizedKeyword}\\b`, 'i');
            return wordBoundaryRegex.test(normalizedLabel);
          }
          return false;
            })
          : [];
        if (matchedItems.length > 0) {
          matchedItems.forEach((item: any) => {
            const label = item.displayName || item.label || item.name || '';
            const isExactMatch = label.toLowerCase() === keyword.toLowerCase();
            const filter = this.createFilterFromDoc(collection, item);
            if (filter.multi.active) {
              filter.multi.searchOperator = 'OR';
            }
            allMatches.push({
              filter,
              isExactMatch
            });
          });
        }
      }
      // License keywords
      const licenseKeywords: LicenseKeywords = {
        'licensed': { type: 'license', value: 'licensed' },
        'unlicensed': { type: 'license', value: 'unlicensed' },
        'not licensed': { type: 'license', value: 'unlicensed' }
      };
      const lowerKeyword = keyword.toLowerCase().trim();
      if (Object.keys(licenseKeywords).includes(lowerKeyword)) {
        const licenseFilter = licenseKeywords[lowerKeyword];
        return [{
          filter: {
            multi: { active: false, data: [], value: '', searchOperator: 'OR' },
            date: { active: false, type: 'completed', from: 0, to: 0 },
            subFilters: { active: false, filters: [] },
            text: { active: false, value: '', field: 'trackName' },
            activeFilterGroups: {
              multi: false,
              date: false,
              single: false,
              text: false,
              license: true
            },
            license: { active: true, value: licenseFilter.value === 'licensed' }
          },
          isExactMatch: true
        }];
      }
      return allMatches.length > 0 ? allMatches : null;
    } catch (error) {
      console.error('Error searching local cache:', error);
    }
    return null;
  }

  private async searchDatabase(keyword: string, firebase: any): Promise<Array<{filter: SearchFilter, isExactMatch: boolean}> | null> {
    try {
      const collections = ['genres', 'client', 'studio', 'composers', 'creativedirector', 'producer', 'sessionartists', 'agency', 'keys', 'tags'];
      const allMatches: Array<{filter: SearchFilter, isExactMatch: boolean}> = [];
      for (const collectionName of collections) {
        const colRef = collection(firebase.db, collectionName);
        const snapshot = await getDocs(colRef);
        // For genres and tags, only allow exact matches
        const isStrictMatch = collectionName === 'genres' || collectionName === 'tags';
        const matchedDocs = snapshot.docs.filter((doc: any) => {
          const docData = doc.data();
          const label = docData.label || docData.name || docData.displayName || '';
          if (!label) return false;
          const normalizedLabel = label.toLowerCase();
          const normalizedKeyword = keyword.toLowerCase();
          if (normalizedLabel === normalizedKeyword) {
            return true;
          }
          if (!isStrictMatch) {
            return normalizedLabel.includes(normalizedKeyword);
          }
          return false;
        });
        if (matchedDocs.length > 0) {
          matchedDocs.forEach((doc: any) => {
            const docData = doc.data();
            const label = docData.label || docData.name || docData.displayName || '';
            const isExactMatch = label.toLowerCase() === keyword.toLowerCase();
            const filter = this.createFilterFromDoc(collectionName, docData);
            if (filter.multi.active) {
              filter.multi.searchOperator = 'OR';
            }
            allMatches.push({
              filter,
              isExactMatch
            });
          });
        }
      }
      // License keywords
      const licenseKeywords: LicenseKeywords = {
        'licensed': { type: 'license', value: 'licensed' },
        'unlicensed': { type: 'license', value: 'unlicensed' },
        'not licensed': { type: 'license', value: 'unlicensed' }
      };
      const lowerKeyword = keyword.toLowerCase().trim();
      if (Object.keys(licenseKeywords).includes(lowerKeyword)) {
        const licenseFilter = licenseKeywords[lowerKeyword];
        return [{
          filter: {
            multi: { active: false, data: [], value: '', searchOperator: 'OR' },
            date: { active: false, type: 'completed', from: 0, to: 0 },
            subFilters: { active: false, filters: [] },
            text: { active: false, value: '', field: 'trackName' },
            activeFilterGroups: {
              multi: false,
              date: false,
              single: false,
              text: false,
              license: true
            },
            license: { active: true, value: licenseFilter.value === 'licensed' }
          },
          isExactMatch: true
        }];
      }
      return allMatches.length > 0 ? allMatches : null;
    } catch (error) {
      console.error('Error searching database:', error);
    }
    return null;
  }

  private async convertMappingToFilter(mapping: MappingItem): Promise<SearchFilter> {
    const filter: SearchFilter = {
      multi: {
        active: false,
        data: [],
        value: 'tags',
        searchOperator: 'OR'
      },
      date: {
        active: false,
        from: 0,
        to: 0,
        type: 'completed'
      },
      subFilters: {
        active: false,
        filters: []
      },
      text: {
        active: false,
        value: '',
        field: ''
      },
      activeFilterGroups: {
        multi: false,
        date: false,
        single: false,
        text: false,
        license: false
      },
      license: {
        active: false,
        value: false
      }
    };

    switch (mapping.filterType) {
      case 'genre':
        filter.subFilters.active = true;
        filter.subFilters.filters.push({
          key: 'genre',
          path: GENRESPATH,
          value: { 
            label: mapping.value,
            value: mapping.value
          }
        });
        break;
      case 'bpm':
        filter.subFilters.active = true;
        filter.subFilters.filters.push({
          key: 'bpm',
          path: 'bpm',
          value: { 
            label: `${mapping.value.min}-${mapping.value.max}`,
            value: `${mapping.value.min}-${mapping.value.max}`
          }
        });
        break;
      case 'tags':
        filter.multi.active = true;
        filter.multi.data = Array.isArray(mapping.value) 
          ? mapping.value.map((tag: any) => ({ label: tag, value: tag }))
          : [{ label: mapping.value, value: mapping.value }];
        break;
      default:
        filter.subFilters.active = true;
        filter.subFilters.filters.push({
          key: mapping.filterType,
          path: mapping.filterType,
          value: {
            label: mapping.value,
            value: mapping.value
          }
        });
    }

    return filter;
  }

  private createFilterFromDoc(collection: string, doc: any): SearchFilter {
    const filter: SearchFilter = {
      multi: {
        active: false,
        data: [],
        value: 'tags',
        searchOperator: 'OR'
      },
      date: {
        active: false,
        from: 0,
        to: 0,
        type: 'completed'
      },
      subFilters: {
        active: false,
        filters: []
      },
      text: {
        active: false,
        value: '',
        field: ''
      },
      activeFilterGroups: {
        multi: false,
        date: false,
        single: false,
        text: false,
        license: false
      },
      license: {
        active: false,
        value: false
      }
    };

    // Map collection names to their correct filter keys and paths
    const keyMap: { [key: string]: { key: string; path: string; isMulti: boolean } } = {
      'genres': { key: 'genre', path: GENRESPATH, isMulti: false },
      'client': { key: 'client', path: CLIENTPATH, isMulti: false },
      'studio': { key: 'studio', path: 'studio', isMulti: false },
      'composers': { key: 'composer', path: 'composers', isMulti: false },
      'creativedirector': { key: 'creativedirector', path: 'creativedirector', isMulti: true },
      'producer': { key: 'producer', path: 'producer', isMulti: false },
      'sessionartists': { key: 'sessionartists', path: 'sessionartists', isMulti: true },
      'agency': { key: 'agency', path: 'agency', isMulti: false },
      'bpm': { key: 'bpm', path: 'bpm', isMulti: false },
      'keys': { key: 'key', path: 'keys', isMulti: false },
      'tags': { key: 'tags', path: 'tags', isMulti: true }
    };

    const mapping = keyMap[collection] || { key: collection, path: collection, isMulti: false };

    const label = doc.label || doc.name || doc.displayName || '';
    
    if (mapping.isMulti) {
      // Handle multi-filters (tags, creativedirector, sessionartists)
      filter.multi.active = true;
      filter.multi.data = [{ label: label, value: label }];
      filter.multi.value = mapping.key;
    } else {
      // Handle single filters
      filter.subFilters.active = true;
      filter.subFilters.filters.push({
        key: mapping.key,
        path: mapping.path,
        value: {
          label: label,
          value: label
        }
      });
    }

    return filter;
  }

  calculateConfidence(matches: DirectMatch[]): number {
    if (matches.length === 0) return 0;
    
    const weights = {
      direct: 1.0,
      mapped: 0.8,
      context: 0.6
    };
    
    const totalWeight = matches.reduce((sum, match) => 
      sum + weights[match.matchType], 0);
    
    const weightedSum = matches.reduce((sum, match) => 
      sum + (match.confidence * weights[match.matchType]), 0);
    
    return weightedSum / totalWeight;
  }
  
  async mapFiltersToSearchQuery(filters: any[]): Promise<SearchFilter> {
    const searchFilter: SearchFilter = {
      multi: {
        active: false,
        data: [],
        value: 'tags',
        searchOperator: 'OR'
      },
      date: {
        active: false,
        from: 0,
        to: 0,
        type: 'completed'
      },
      subFilters: {
        active: false,
        filters: []
      },
      text: {
        active: false,
        value: '',
        field: ''
      },
      activeFilterGroups: {
        multi: false,
        date: false,
        single: false,
        text: false,
        license: false
      },
      license: {
        active: false,
        value: false
      }
    };

    // Helper function to properly format string values
    const formatValue = (value: string): string => {
      return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
    };
    
    // Group filters by type to handle multiple values of the same type
    const groupedFilters: { [key: string]: any[] } = {};
    
    for (const filter of filters) {
      // Handle both old format {key, path, value} and new format {type, value}
      const filterType = filter.type || filter.key;
      const filterValue = filter.value?.value || filter.value;
      
      if (!groupedFilters[filterType]) {
        groupedFilters[filterType] = [];
      }
      groupedFilters[filterType].push(filterValue);
    }
    
    // Determine multi-filter priority: tags2 > tags > tag > others
    let multiFilterType: string | null = null;
    let multiFilterValues: any[] = [];
    
    // Check in priority order
    if (groupedFilters.tags2) {
      multiFilterType = 'tags2';
      multiFilterValues = groupedFilters.tags2;
    } else if (groupedFilters.tags) {
      multiFilterType = 'tags';
      multiFilterValues = groupedFilters.tags;
    } else if (groupedFilters.tag) {
      multiFilterType = 'tags';  // Convert 'tag' to 'tags'
      multiFilterValues = groupedFilters.tag;
    } else if (groupedFilters.mood) {
      multiFilterType = 'mood';
      multiFilterValues = groupedFilters.mood;
    } else if (groupedFilters.creativedirector) {
      multiFilterType = 'creativedirector';
      multiFilterValues = groupedFilters.creativedirector;
    } else if (groupedFilters.sessionartists) {
      multiFilterType = 'sessionartists';
      multiFilterValues = groupedFilters.sessionartists;
    }
    
    // Set up multi-filter if one was found
    if (multiFilterType && multiFilterValues.length > 0) {
      searchFilter.multi.active = true;
      searchFilter.multi.value = multiFilterType;
      searchFilter.multi.data = multiFilterValues.map(value => ({
        label: typeof value === 'string' ? (multiFilterType === 'tags2' ? value : formatValue(value)) : 'Unknown',
        value: typeof value === 'string' ? (multiFilterType === 'tags2' ? value : formatValue(value)) : 'Unknown'
      }));
    }
    
    // Process sub-filters (all non-multi types)
    for (const [filterType, values] of Object.entries(groupedFilters)) {
      // Skip types that were already processed as multi-filters
      if (['tags2', 'tags', 'tag', 'mood', 'creativedirector', 'sessionartists'].includes(filterType)) {
        continue;
      }
      
      switch (filterType) {
        case 'genre':
          searchFilter.subFilters.active = true;
          values.forEach(value => {
            const genreValue = typeof value === 'string' ? formatValue(value) : 'Unknown';
            searchFilter.subFilters.filters.push({
              key: 'genre',
              path: GENRESPATH,
              value: {
                label: genreValue,
                value: genreValue
              }
            });
          });
          break;
          
        case 'bpm':
          searchFilter.subFilters.active = true;
          values.forEach(value => {
            const bpmValue = typeof value === 'string' ? value : 'Unknown';
            searchFilter.subFilters.filters.push({
              key: 'bpm',
              path: 'bpm',
              value: {
                label: bpmValue,
                value: bpmValue
              }
            });
          });
          break;
          
        case 'key':
          searchFilter.subFilters.active = true;
          values.forEach(value => {
            const keyValue = typeof value === 'string' ? value : 'Unknown';
            searchFilter.subFilters.filters.push({
              key: 'key',
              path: 'keys',
              value: {
                label: keyValue,
                value: keyValue
              }
            });
          });
          break;
          
        case 'client':
          searchFilter.subFilters.active = true;
          values.forEach(value => {
            const clientValue = typeof value === 'string' ? formatValue(value) : 'Unknown';
            searchFilter.subFilters.filters.push({
              key: 'client',
              path: CLIENTPATH,
              value: {
                label: clientValue,
                value: clientValue
              }
            });
          });
          break;
          
        case 'composer':
          searchFilter.subFilters.active = true;
          values.forEach(value => {
            const composerValue = typeof value === 'string' ? formatValue(value) : 'Unknown';
            searchFilter.subFilters.filters.push({
              key: 'composer',
              path: 'composers',
              value: {
                label: composerValue,
                value: composerValue
              }
            });
          });
          break;
      }
    }
    
    // Set active filter groups
    if (searchFilter.multi.active) {
      searchFilter.activeFilterGroups.multi = true;
    }
    if (searchFilter.subFilters.active) {
      searchFilter.activeFilterGroups.single = true;
    }
    
    return searchFilter;
  }
}

export const filterMapper = new FilterMapper();

// Example function using firebase as a parameter
export function filterGenresByKeyword(keyword: string, firebase: any) {
  if (!firebase) return [];
  // Use firebase.db to query genres
  // ...
}